import { Add } from '@mui/icons-material';
import { Grid, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { RegionOverridesFragmentProps } from './RegionOverridesFragment.props';
import { SwitchGrid } from './RegionOverridesFragment.styles';
import { ColorProps, RegionOverridesType } from '../../../../@types';
import { Accordian } from '../../../../components/atoms/Accordian';
import { Button, ButtonVariantProps } from '../../../../components/atoms/Button';
import { FormControlLabel, FormControlLabelPlacementProps } from '../../../../components/atoms/FormControlLabel';
import { DropdownItem, SelectMenu } from '../../../../components/atoms/SelectMenu';
import { Switch } from '../../../../components/atoms/Switch';
import { TextField } from '../../../../components/atoms/TextField';
import { ToogleButtonTextAlignment } from '../../../../components/atoms/ToogleButtonGroup';
import { GRID_SPACING } from '../../../../configs/ui-constants';
import { SAMPLE_REGION_OVERRIDES } from '../../../../constants/structure';
import { useGetSuburbsByPostcode } from '../../../../queries/geosocial-query';
import { useGetRegionTypes } from '../../../../queries/regionTypes-query';


export const RegionOverridesFragment: React.FC<RegionOverridesFragmentProps> = ({
  regionOverrides,
  onChange,
  error
}) => {
  const [expanded, setExpanded] = useState(true);
  const [regionTypes, setRegionTypes] = useState<Array<DropdownItem>>([]);
  const [suburbNamesMap, setSuburbNamesMap] = useState<Record<number, DropdownItem[]>>({});

  const getRegionTypes = useGetRegionTypes();
  const getSuburbsByPostcode = useGetSuburbsByPostcode();

  useEffect(() => {
    if (getRegionTypes.data) {
      const regionTypesData = getRegionTypes.data.map((regionType) => ({
        label: regionType.name,
        value: regionType.code
      }));

      setRegionTypes(regionTypesData);
    }
  }, [getRegionTypes.data]);


  const handleAdd = (event: any) => {
    event.stopPropagation();
    const updatedDataArray = [...regionOverrides];
    updatedDataArray.push({
      ...SAMPLE_REGION_OVERRIDES[0],
      overrideId: regionOverrides.length > 0 ? Math.max(...regionOverrides.map((data) => data.overrideId)) + 1 : 0
    });
    onChange(updatedDataArray);
  };

  const fetchSuburbsByPostcode = async (postCode: string) => {
    const suburbs: any = postCode && await getSuburbsByPostcode.mutateAsync(postCode);
    return suburbs || [];
  };

  const updateData = async (overrideId: number, fieldData: RegionOverridesType, fieldName: string, selectedValue: any) => {
    let updatedArray = [...regionOverrides];

    // If postcode changed, fetch new suburb names for that row
    if (fieldName === 'postCode-onBlur' && selectedValue) {
      const suburbs = await fetchSuburbsByPostcode(selectedValue);
      const suburbNamesData = suburbs.map((s: any) => ({
        label: s.locality,
        value: s.locality
      }));

      setSuburbNamesMap((prev) => ({
        ...prev,
        [overrideId]: suburbNamesData
      }));
    } else if (fieldName === 'postCode') {
      updatedArray = updatedArray.map((regionOverride) =>
        regionOverride.overrideId === overrideId
          ? { ...fieldData, postCode: selectedValue, suburbName: '' }
          : regionOverride
      );
      onChange(updatedArray);

      // Also clear existing suburb names for that row
      setSuburbNamesMap((prev) => ({
        ...prev,
        [overrideId]: [],
      }));
    } else {
      updatedArray = updatedArray.map((regionOverride) =>
        regionOverride.overrideId === overrideId
          ? { ...fieldData, [fieldName]: selectedValue }
          : regionOverride
      );
      onChange(updatedArray);
    }
  };

  const handleSuburbDropdownOpen = async (regionOverride: RegionOverridesType) => {
    const { postCode, overrideId } = regionOverride;

    // If already loaded or no postcode, skip
    if (!postCode || suburbNamesMap[overrideId]?.length) return;

    const suburbs = await fetchSuburbsByPostcode(postCode);
    const suburbNamesData = suburbs.map((s: any) => ({
      label: s.locality,
      value: s.locality
    }));

    setSuburbNamesMap((prev) => ({
      ...prev,
      [overrideId]: suburbNamesData
    }));
  };

  return (
    <>
      <Accordian
        title={
          <Grid container justifyContent="space-between" width="100%">
            <Grid mr={2}>
              <Typography>Region Overrides</Typography>
            </Grid>
            <Grid mr={2}>
              <Button variant={ButtonVariantProps.Secondary} startIcon={<Add />} onClick={handleAdd}>
                  Add
              </Button>
            </Grid>
          </Grid>
        }
        onChange={(_, newExpanded: boolean) => setExpanded(newExpanded)}
        expanded={expanded}
      >
        {regionOverrides?.map((regionOverride) =>
          <Stack key={regionOverride.overrideId} direction="row" spacing={GRID_SPACING} alignItems={ToogleButtonTextAlignment.Center}>
            <TextField
              required
              label="Postode"
              value={regionOverride.postCode}
              onChange={(postCode: string) => updateData(regionOverride.overrideId, regionOverride, 'postCode', postCode)}
              onBlur={(postCode: string) => updateData(regionOverride.overrideId, regionOverride, 'postCode-onBlur', postCode)}
              error={regionOverride.postCode ? false : error}
            />
            <SelectMenu
              id="suburb-name"
              label="Suburb Name*"
              minWidth={200}
              items={suburbNamesMap[regionOverride.overrideId] || [{
                label: regionOverride?.suburbName,
                value: regionOverride?.suburbName
              }]}
              selectedValue={regionOverride.suburbName}
              onChange={(suburbName: string) => updateData(regionOverride.overrideId, regionOverride, 'suburbName', suburbName)}
              onOpen={() => handleSuburbDropdownOpen(regionOverride)}
              validate={error}
              helperTextEnabled={false}
            />
            <SelectMenu
              id="region-override"
              label="Region Override*"
              minWidth={200}
              items={regionTypes}
              selectedValue={regionOverride.regionOverride}
              onChange={(regionOverrideValue: string) => updateData(regionOverride.overrideId, regionOverride, 'regionOverride', regionOverrideValue)}
              validate={error}
              helperTextEnabled={false}
            />
            <SwitchGrid>
              <FormControlLabel
                control={
                  <Switch
                    color={ColorProps.Success}
                    onChange={(event) => updateData(regionOverride.overrideId, regionOverride, 'isActive', event.target.checked)}
                    checked={regionOverride.isActive} />
                }
                label="Active"
                labelPlacement={FormControlLabelPlacementProps.Top}
              />
            </SwitchGrid>
          </Stack>
        )}
      </Accordian>
    </>
  );
};